import axios from "axios";
import { reformatDate } from "@/helpers/workWithDates";
import {
    formatCandidateForTableRecord,
    getFormDataForStatusChange,
    getQueryForGlobalCandidatesFilters,
    getQueryParamsForFetchingCandidates,
} from "@/helpers/candidatesFunctions";
import serialize from "json-form-data";
import { getApi } from "../utils";
import {addAuthKey} from '@/helpers/addAuthKeyToUrl';

const apiUrl = `${process.env.VUE_APP_BASE_URL || '/'}index.php`;

const columnNames = {
    phone: {
        full: "Телефон кандидата",
    },
    address: {
        full: "Адрес кандидата",
    },
    experience: {
        full: "Опыт работы в сфере",
    },
    postCategorySpecialty: {
        short: "Должность/Спец-ть",
    },
    citizenship: {
        short: "Гражд-во",
    },
    dateOfStatusChange: {
        short: "Статус (дата изменения)",
    },
};

const state = () => ({
    columns: [],
    filters: [],
    userColumns: [],
    userFilters: [],
    records: [],
    candidateEntities: [],
    collections: {
        citizenships: [],
        candidateStatuses: [],
        positions: [],
        filials: [],
        workers: [],
        accountablePeople: [],
        accountablePeopleAll: [],
        questCompletionStatuses: [],
        sources: [],
    },
    candidatesPositionsCount: {},
    queryParamsForGoogleSheets: "",
    totalItemsCount: 0,

    canbanSettings: {
        enabledStatuses: [1, 9, 8, 7, 4, 12, 6, 13, 2, 10, 5, 23],
        highlightedColumn: "",
    },
});

const getters = {
    getEnabledCanbanStatuses: (state) =>
        state.collections.candidateStatuses.filter((status) =>
            state.canbanSettings.enabledStatuses.includes(status.value),
        ),
    getRecordsLengthByPosition: (state) => (positionId) =>
        state.records.reduce((acc, currentCandidate) => {
            const { enabledStatuses } = state.canbanSettings;
            const positionMatched = currentCandidate.postCategorySpecialty.position.value === positionId;
            if (
                positionMatched &&
                enabledStatuses.includes(currentCandidate.dateOfStatusChange.candidateStatus.value)
            ) {
                acc += 1;
            }
            return acc;
        }, 0),
    getCandidateById: (state) => (candidateId) => state.candidateEntities.find((c) => c.id === candidateId),
    getCandidateRecordById: (state) => (candidateId) => state.records.find((c) => c.id === candidateId),
    getCandidateWorksheet: (state) => (candidateId) => {
        const target = state.records.find((c) => c.id === candidateId);

        if (!target) {
            return null;
        }

        return target._meta;
    },
};

const actions = {
    async fetchTableColumns({ commit, rootState }) {
        const { authToken: token, userId } = rootState;
        const url = addAuthKey(`${apiUrl}?r=api/candidate/columns&user_id=${userId}`, token);

        const { data } = await axios.get(url);

        const sortingOrder = [
            "created",
            "fullName",
            "phone",
            "source",
            "dateOfStatusChange",
            "experience",
            "postCategorySpecialty",
            "filial",
            "citizenship",
            "address",
            "invitedBy",
        ];

        const columns = data
            .map((column) => ({
                id: column.code,
                label:
                    columnNames[column.code] && columnNames[column.code].short
                        ? columnNames[column.code].short
                        : column.short_name,
                fullName:
                    columnNames[column.code] && columnNames[column.code].full
                        ? columnNames[column.code].full
                        : column.name,
                type: column.data_type,
                level: column.level,
                isCategory: column.is_category,
                disabled: column.disabled,
            }))
            .sort((a, b) => sortingOrder.indexOf(a.id) - sortingOrder.indexOf(b.id));

        commit("setColumns", columns);
    },
    async fetchTableUserColumns({ rootState, commit }) {
        const token = rootState.authToken;
        const { userId } = rootState;
        const url = addAuthKey(`${apiUrl}?r=api/candidate/user-columns&user_id=${userId}`, token);

        const { data } = await axios.get(url);
        commit("setUserColumns", data.columns);
    },
    async fetchTableRecords({ rootState, commit }, filters = {}) {
        const token = rootState.authToken;
        const { userId } = rootState;
        const { page } = filters;

        const queryParams = getQueryParamsForFetchingCandidates(filters);
        const stringifiedQueryParams = queryParams.join("&");

        commit("setQueryForGoogleSheets", stringifiedQueryParams);

        const url = addAuthKey(`${apiUrl}?r=api/candidate/index&user_id=${userId}&${stringifiedQueryParams}`, token);

        const { data, headers } = await axios.get(url);

        commit("setTotalItemsCount", +headers["x-pagination-total-count"]);

        const candidates = data.map((c) => ({
            ...c,
            dateOfStatusChange: reformatDate(c.dateOfStatusChange),
        }));

        const tableRecords = data.map((candidate) => formatCandidateForTableRecord(candidate));
        
        commit("setCandidateEntities", { entities: candidates, page });
        commit("setRecords", { records: tableRecords, page });
        
        return data;
    },
    async fetchCandidateStatuses({ commit, rootState }) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}?r=api/candidate-status/index&expand=reasons&status_type=active&sort=-sort`, token);

        const { data } = await axios.get(url);
        commit(
            "setCandidateStatuses",
            data.map((s) => ({ label: s.name, value: s.id, reasons: s.reasons })),
        );
    },
    async fetchCandidateSources({ commit, rootState }) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}?r=api/candidate/source-list`, token);

        const { data } = await axios.get(url);

        const sources = data.map((s) => ({ label: s.name, value: s.id }));
        commit("setSourcesCollection", sources);
    },
    async changeCandidateStatus({ rootState, commit }, formValues) {
        const { candidateStoreIndex, candidateId } = formValues;

        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}?r=api/candidate/save-status&id=${candidateId}`, token);
        const formData = getFormDataForStatusChange(formValues);

        const { data } = await axios.put(url, formData);

        if (data.success) {
            commit("changeCandidateStatus", { candidateStoreIndex, ...data.model });
        }
    },
    async saveUserColumns({ commit, rootState }, columns) {
        const token = rootState.authToken;
        const { userId } = rootState;
        const formData = new FormData();

        formData.append("columns", JSON.stringify(columns));
        formData.append("user_id", userId);

        const url = addAuthKey(`${apiUrl}?r=api/candidate/save-user-columns&user_id=${userId}`, token);

        await axios.post(url, formData);
        commit("setUserColumns", columns);
    },
    async fetchCitizenships({ rootState, commit }) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}?r=api/directory/citizenship`, token);

        const { data } = await axios.get(url);

        const citizenships = data.map((citizenship) => ({
            label: citizenship.name,
            value: citizenship.id,
        }));

        commit("setCitizenshipsCollection", citizenships);
    },
    async fetchQuestCompletionStatuses({ rootState, commit }) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}?r=api/candidate/quest-completion`, token);

        const { data } = await axios.get(url);

        const statuses = Object.entries(data).map(([statusId, status]) => ({
            label: status,
            value: statusId,
        }));

        commit("setQuestCompletionStatuses", statuses);
    },
    async fetchPositions({ rootState, commit }) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}?r=api/directory/position`, token);

        const { data } = await axios.get(url);

        const positions = Object.entries(data).map(([positionCode, { name, categories }]) => {
            let ranks = categories;

            if (Object.keys(ranks).length) {
                ranks = Object.entries(ranks).map(([rankId, rank]) => ({
                    label: rank,
                    value: rankId,
                }));
            }

            return {
                label: name,
                value: positionCode,
                ranks,
            };
        });

        commit("setPositionsCollection", positions);
    },
    async fetchFilials({ rootState, commit }) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}?r=api/filial/index`, token);

        const { data } = await axios.get(url);

        const filials = data.items.map((filial) => ({ label: filial.filial_name, value: filial.id }));

        commit("setFilialsCollection", filials);
    },
    async fetchAccountablePeople({ rootState, commit }) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}?r=api/candidate/accountable`, token);

        const { data } = await axios.get(url);

        const accountables = data.map((accountable) => ({
            ...accountable,
            label: accountable.name,
            value: accountable.id,
        }));

        commit("setAccountablePeople", accountables);
    },
    async fetchAccountablePeopleAll({ rootState, commit }) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}?r=api/candidate/accountable&used=0`, token);

        const { data } = await axios.get(url);

        const accountables = data.map((accountable) => ({
            ...accountable,
            label: accountable.name,
            value: accountable.id,
        }));

        commit("setAccountablePeopleAll", accountables);
    },
    async fetchWorkers({ rootState, commit }) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}?r=api/worker/index&pagination=0&sort=name&job_status=1`, token);

        const { data } = await axios.get(url);
        const workers = data.map((worker) => ({
            label: worker.name,
            value: worker.id,
            filialId: worker.filial_id,
        }));

        commit("setWorkersCollection", workers);
    },
    async fetchFilters({ rootState, commit }) {
        const token = rootState.authToken;
        const { userId } = rootState;
        const url = addAuthKey(`${apiUrl}?r=api/candidate/filters&user_id=${userId}`, token);

        const { data } = await axios.get(url);

        commit("setFilters", data);
    },
    async fetchUserFilters({ rootState, commit }) {
        const token = rootState.authToken;
        const { userId } = rootState;
        const url = addAuthKey(`${apiUrl}?r=api/candidate/user-filters&user_id=${userId}`, token);

        const { data } = await axios.get(url);

        commit("setUserFilters", data.filters);
    },
    async saveUserFilters({ rootState, commit }, userFilters) {
        const { userId, authToken: token } = rootState;
        const url = addAuthKey(`${apiUrl}?r=api/candidate/save-user-filters`, token);
        const formData = new FormData();

        formData.append("filters", JSON.stringify(userFilters));
        formData.append("user_id", userId);

        await axios.put(url, formData);

        commit("updateUserFilters", userFilters);
    },
    async deleteCandidate({ rootState, commit }, candidateId) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}?r=api/candidate/delete&id=${candidateId}`, token);

        const { data } = await axios.delete(url);

        if (data.success) {
            commit("deleteCandidate", candidateId);
        }
    },
    async fetchCandidateInformation({ rootState }, candidateId) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}?r=api/candidate/view&id=${candidateId}&expand=history,files&sort=-time`, token);

        const { data } = await axios.get(url);

        return data;
    },
    async saveCandidateComment({ rootState, commit }, { candidateId, candidateComment }) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}?r=api/candidate/save-comment&id=${candidateId}`, token);
        const formData = new FormData();

        formData.append("comment", candidateComment);

        const { data } = await axios.put(url, formData);

        if (data.success) {
            commit("setCandidateComment", { candidateId, candidateComment });
        }
    },
    setCanbanSettings({ commit }, settings) {
        localStorage.setItem("canbanSettings", JSON.stringify(settings));
        commit("setCanbanSettings", settings);
    },
    async fetchCandidatesPositionsCount({ rootState, commit }, filters) {
        const token = rootState.authToken;
        const queryParams = getQueryForGlobalCandidatesFilters(filters);
        const url = addAuthKey(`${apiUrl}?r=api/candidate/count&${queryParams.join("&")}`, token);

        const { data } = await axios.get(url);

        commit("setCandidatesPositionsCount", data);
    },
    async uploadFile({ rootState }, { files, candidateId }) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}?r=api/candidate/save-files&id=${candidateId}`, token);

        const formData = new FormData();

        files.forEach((file) => {
            formData.append("files[]", file);
        });

        await axios.post(url, formData);
    },
    async deleteFile({ rootState }, { fileName, candidateId }) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}?r=api/candidate/delete-file&id=${candidateId}&file=${fileName}`, token);

        await axios.delete(url);
    },
    async downloadAllFiles({ rootState }, candidateId) {
        const token = rootState.authToken;
        const url = addAuthKey(`${apiUrl}?r=api/candidate/download-files-archive&id=${candidateId}`, token);

        const { data } = await axios.get(url);

        return data.link;
    },
    async getUserProfile(ctx, id) {
        const { url } = getApi(ctx);

        const { data } = await axios.get(url("api/profile-value/candidate-profile", { worker: 0, id }));

        return data;
    },
    async setUserProfile(ctx, id) {
        const { url } = getApi(ctx);

        const { data } = await axios.get(url("api/profile-value/set-profile", { id }));

        return data;
    },
    async getUserProfilePositions(ctx, id) {
        const { url } = getApi(ctx);

        const { data } = await axios.get(url("api/profile-value/post-select", { worker: 0, id }));

        return data;
    },
    async getUserProfileData(ctx, id) {
        const { url } = getApi(ctx);

        const { data } = await axios.get(url("api/profile-value/view", { worker: 0, id }));

        return data;
    },
    async saveUserProfileData(ctx, params) {
        const { url } = getApi(ctx);
        const { id, values, files } = params;

        const fd = serialize({ values });

        Object.entries(files).forEach(([key, value]) => {
            value.forEach((file) => {
                fd.append(`files${key}[]`, file);
            });
        });

        const { data } = await axios.post(url("api/profile-value/save-values", { id, worker: 0 }), fd);

        return data;
    },
    async saveCustomProfileVariants(ctx, dictionaries) {
        const collection = {};

        if (!dictionaries.length) return collection;

        const { url } = getApi(ctx);

        const list = dictionaries.map((dictionary) => {
            const { fieldId, code, items } = dictionary;
            return code ? { code, values: items } : { field_id: fieldId, values: items };
        });

        const { data } = await axios.post(url("api/dictionary/multiple-save"), list);

        data.forEach((items) => {
            items.forEach((item) => {
                const { profile_field_id, code, name, id } = item;
                const dictionaryCode = code || profile_field_id;
                if (!collection[dictionaryCode]) {
                    collection[dictionaryCode] = {};
                }

                collection[dictionaryCode][name] = id;
            });
        });

        return collection;
    },
};

const mutations = {
    setCandidateEntities(state, { entities, page }) {
        if (page === 1) {
            state.candidateEntities = entities;
            return;
        }
        state.candidateEntities = [...state.candidateEntities, ...entities];
    },
    setCanbanSettings(state, settings) {
        state.canbanSettings = settings;
    },
    setColumns(state, columns) {
        state.columns = columns;
    },
    setUserColumns(state, columns) {
        state.userColumns = columns;
    },
    setCandidateSources(state, sources) {
        state.sources = sources;
    },
    setRecords(state, { records, page }) {
        if (page === 1) {
            state.records = records.map((record, index) => ({ ...record, storeIndex: index }));
            return;
        }
        state.records = [...state.records, ...records.map((record, index) => ({ ...record, storeIndex: index }))];
    },
    deleteCandidate(state, candidateId) {
        const candidateRecordIndex = state.records.findIndex((candidate) => candidate.id === candidateId);
        const candidateEntityIndex = state.candidateEntities.findIndex((candidate) => candidate.id === candidateId);

        state.records.splice(candidateRecordIndex, 1);
        state.candidateEntities.splice(candidateEntityIndex, 1);
    },
    setStatusValues(state, values) {
        const { rejectionReasons } = values;
        state.rejectionReasons = rejectionReasons;
    },
    changeCandidateStatus(state, updatedCandidate) {
        let { candidateStoreIndex, id: candidateId } = updatedCandidate;
        if (candidateStoreIndex==undefined) {
            candidateStoreIndex = state.records.findIndex((c)=>c.id==candidateId)
        }
        const entity = state.candidateEntities.find((c) => c.id === candidateId);

        if (!entity) {
            return;
        }

        Object.keys(updatedCandidate).forEach((key) => {
            if (!updatedCandidate[key]) {
                return;
            }
            if (key === "dateOfStatusChange") {
                entity[key] = reformatDate(updatedCandidate[key]);
                return;
            }
            if (typeof updatedCandidate[key] !== "string" && Object.keys(updatedCandidate[key]).length) {
                entity[key] = { ...updatedCandidate[key] };
                return;
            }
            entity[key] = updatedCandidate[key];
        });

        const updatedTableRecord = formatCandidateForTableRecord(updatedCandidate);
        const record = state.records[candidateStoreIndex];


        Object.keys(updatedTableRecord).forEach((key) => {
            if (!updatedTableRecord[key]) {
                return;
            }
            if (key === "dateOfStatusChange") {
                record[key] = {
                    ...updatedTableRecord[key],
                    candidateStatus: {
                        ...updatedTableRecord[key].candidateStatus,
                        date: reformatDate(updatedCandidate[key]),
                    },
                };
                return;
            }
            if (typeof updatedTableRecord[key] !== "string" && Object.keys(updatedTableRecord[key]).length) {
                record[key] = { ...updatedTableRecord[key] };
                return;
            }
            record[key] = updatedTableRecord[key];
        });

    },
    setCitizenshipsCollection(state, citizenships) {
        state.collections.citizenships = citizenships;
    },
    setCandidateStatuses(state, statuses) {
        state.collections.candidateStatuses = statuses;
    },
    setQuestCompletionStatuses(state, statuses) {
        state.collections.questCompletionStatuses = statuses;
    },
    setPositionsCollection(state, positions) {
        state.collections.positions = positions;
    },
    setFilialsCollection(state, filials) {
        state.collections.filials = filials;
    },
    setAccountablePeople(state, people) {
        state.collections.accountablePeople = people;
    },
    setAccountablePeopleAll(state, people) {
        state.collections.accountablePeopleAll = people;
    },
    setSourcesCollection(state, sources) {
        state.collections.sources = sources;
    },
    setWorkersCollection(state, workers) {
        state.collections.workers = workers;
    },
    setFilters(state, filters) {
        state.filters = filters;
    },
    setUserFilters(state, filters) {
        state.userFilters = filters;
    },
    updateUserFilters(state, userFilters) {
        state.userFilters = userFilters;
    },
    setCandidateComment(state, { candidateId, candidateComment }) {
        const candidate = state.candidateEntities.find((candidate) => candidate.id === candidateId);
        const candidateRecord = state.records.find((candidate) => candidate.id === candidateId);

        candidate.comment = candidateComment;
        candidateRecord.candidateComment.value = candidateComment;
    },
    setCandidatesPositionsCount(state, data) {
        state.candidatesPositionsCount = data;
    },
    setQueryForGoogleSheets(state, query) {
        state.queryParamsForGoogleSheets = query;
    },
    setTotalItemsCount(state, amount) {
        state.totalItemsCount = amount;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
